<template>
  <div class="container-fluid mt-5 page-padding margin_80_35">
    <div class="row pt-3" v-if="currentFaq">
      <div class="col-lg-9 col-md-12">
        <nav class="faq-breadcrumb" aria-label="breadcrumb">
          <router-link to="/home">Home</router-link>
          <span class="faq-breadcrumb-sep">/</span>
          <router-link to="/faq">FAQ</router-link>
          <span class="faq-breadcrumb-sep">/</span>
          <router-link :to="'/faq/' + currentFaq.CategoryId">{{ currentFaq.CategoryTitle }}</router-link>
          <span class="faq-breadcrumb-sep">/</span>
          <span class="faq-breadcrumb-current">{{ currentFaq.Question }}</span>
        </nav>

        <h1 class="faq-article-title">{{ currentFaq.Question }}</h1>

        <span @click="onTagClick(item)" v-for="(item, index) in currentFaq.KeyWordList" :key="index"
          class="badge badge-pill badge-secondary mr-1 cursor-pointer">#{{ item }}</span>

        <div class="faq-container pt-3" v-html="lazyAnswer(currentFaq.Answer)"></div>

        <div class="faq-article-nav">
          <router-link v-if="previousFaq" class="faq-article-nav-link faq-article-nav-prev"
            :to="'/faq/' + previousFaq.CategoryId + '/' + previousFaq.Id">
            <i class="fa fa-angle-left"></i>
            <span>{{ previousFaq.Question }}</span>
          </router-link>
          <router-link v-if="nextFaq" class="faq-article-nav-link faq-article-nav-next"
            :to="'/faq/' + nextFaq.CategoryId + '/' + nextFaq.Id">
            <span>{{ nextFaq.Question }}</span>
            <i class="fa fa-angle-right"></i>
          </router-link>
        </div>
      </div>

      <aside class="col-lg-3 col-md-12 bg-white h-100">
        <div class="widget">
          <div class="widget-title">
            <h4>Share this answer</h4>
          </div>
          <div class="share-buttons">
            <button @click="copyLink" class="share-btn copy-link" title="Copy link">
              <i class="fa" :class="linkCopied ? 'fa-check' : 'fa-link'"></i>
              <span>{{ linkCopied ? 'Link copied!' : 'Copy link' }}</span>
            </button>
          </div>
        </div>

        <div class="widget">
          <div class="widget-title">
            <h4>Still need help?</h4>
          </div>
          <p class="still-need-help-text">If this didn't answer your question, our support team can help.</p>
          <router-link to="/support-request" class="share-btn support-request">
            <i class="fa fa-life-ring"></i>
            <span>Contact Support</span>
          </router-link>
        </div>

        <div class="widget" v-if="relatedFaqs.length > 0">
          <div class="widget-title">
            <h4>More in {{ currentFaq.CategoryTitle }}</h4>
          </div>
          <ul class="comments-list">
            <li v-for="related in relatedFaqs" :key="related.Id">
              <router-link :to="'/faq/' + related.CategoryId + '/' + related.Id">{{ related.Question }}</router-link>
            </li>
          </ul>
        </div>
      </aside>
    </div>

    <div class="row pt-3" v-else-if="loaded">
      <div class="col-12 text-center py-5">
        <p>This question could not be found.</p>
        <router-link to="/faq">Back to FAQ</router-link>
      </div>
    </div>

    <ImageLightbox :visible="lightboxVisible" :src="lightboxSrc" @close="lightboxVisible = false" />
  </div>
</template>

<script>
import FAQService from '../services/faq-service'
import ImageLightbox from '../components/Common/ImageLightbox.vue'

export default {
  name: 'FaqArticle',
  components: { ImageLightbox },
  data() {
    return {
      currentFaq: null,
      categoryFaqs: [],
      loaded: false,
      lightboxVisible: false,
      lightboxSrc: '',
      linkCopied: false
    }
  },
  computed: {
    relatedFaqs() {
      if (!this.currentFaq) return []
      var currentId = this.currentFaq.Id
      return this.categoryFaqs.filter(function (f) { return f.Id !== currentId }).slice(0, 8)
    },
    currentIndex() {
      if (!this.currentFaq) return -1
      var currentId = this.currentFaq.Id
      return this.categoryFaqs.findIndex(function (f) { return f.Id === currentId })
    },
    previousFaq() {
      if (this.currentIndex <= 0) return null
      return this.categoryFaqs[this.currentIndex - 1]
    },
    nextFaq() {
      if (this.currentIndex === -1 || this.currentIndex >= this.categoryFaqs.length - 1) return null
      return this.categoryFaqs[this.currentIndex + 1]
    }
  },
  watch: {
    '$route.params.faqId'() {
      this.fetchFaq()
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    '$route.params.categoryId'() {
      this.fetchCategoryFaqs()
    }
  },
  mounted() {
    window.scrollTo(0, 0)
    this.fetchFaq()
    this.fetchCategoryFaqs()
  },
  methods: {
    fetchFaq() {
      if (!this.$route.params.faqId) return
      this.loaded = false
      FAQService.getFaqById(this.$route.params.faqId).then(response => {
        this.currentFaq = (response.data.Status && response.data.Object) ? response.data.Object.faq : null
        this.loaded = true
        this.applyMeta(this.currentFaq)
        this.$nextTick(() => this.enhanceFaqImages())
      }, (error) => {
        console.log(error)
        this.currentFaq = null
        this.loaded = true
      })
    },
    fetchCategoryFaqs() {
      if (!this.$route.params.categoryId) return
      FAQService.getFilteredFaqList(true, this.$route.params.categoryId, null).then(response => {
        this.categoryFaqs = response.data.Status ? response.data.Object.faqs : []
      }, (error) => {
        console.log(error)
        this.categoryFaqs = []
      })
    },
    applyMeta(faq) {
      if (!faq) return
      document.title = faq.Question + ' | Circleram FAQ'
      var description = faq.Answer ? faq.Answer.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 160) : ''
      var metaTag = document.querySelector('meta[name="description"]')
      if (!metaTag) {
        metaTag = document.createElement('meta')
        metaTag.setAttribute('name', 'description')
        document.head.appendChild(metaTag)
      }
      metaTag.setAttribute('content', description)
    },
    onTagClick(String) {
      this.$store.dispatch('chnageSearchString', String)
    },
    copyLink() {
      var url = window.location.href
      var vm = this
      var showCopied = function () {
        vm.linkCopied = true
        setTimeout(function () { vm.linkCopied = false }, 2000)
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(showCopied)
      } else {
        var textArea = document.createElement('textarea')
        textArea.value = url
        textArea.style.position = 'fixed'
        textArea.style.left = '-999999px'
        document.body.appendChild(textArea)
        textArea.focus()
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
        showCopied()
      }
    },
    openLightbox(src) {
      this.lightboxSrc = src
      this.lightboxVisible = true
    },
    lazyAnswer(html) {
      if (!html) return html
      return html.replace(/<img(?![^>]*\bloading=)/gi, '<img loading="lazy" decoding="async"')
    },
    enhanceFaqImages() {
      var vm = this
      var images = this.$el.querySelectorAll('.faq-container img:not([data-zoom-bound])')
      images.forEach(function (img) {
        img.setAttribute('data-zoom-bound', 'true')
        var wrap = document.createElement('span')
        wrap.className = 'faq-img-wrap'
        img.parentNode.insertBefore(wrap, img)
        wrap.appendChild(img)
        var zoomBtn = document.createElement('button')
        zoomBtn.type = 'button'
        zoomBtn.className = 'faq-img-zoom-btn'
        zoomBtn.setAttribute('aria-label', 'View larger image')
        zoomBtn.innerHTML = '<i class="fa fa-search-plus"></i>'
        zoomBtn.addEventListener('click', function (e) {
          e.stopPropagation()
          vm.openLightbox(img.src)
        })
        wrap.appendChild(zoomBtn)
        img.addEventListener('error', function () {
          zoomBtn.style.display = 'none'
        })
        if (img.complete && img.naturalWidth === 0) {
          zoomBtn.style.display = 'none'
        }
      })
    }
  }
}
</script>

<style scoped>
.faq-breadcrumb {
  font-size: 14px;
  margin-bottom: 16px;
}
.faq-breadcrumb-sep {
  margin: 0 6px;
  color: #999;
}
.faq-breadcrumb-current {
  color: #666;
}
.faq-article-title {
  margin-bottom: 16px;
}
.faq-article-nav {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-top: 40px;
  padding-top: 20px;
  border-top: 1px solid #ededed;
}
.faq-article-nav-link {
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 48%;
}
.faq-article-nav-next {
  margin-left: auto;
  text-align: right;
  justify-content: flex-end;
}
.share-buttons {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.share-btn {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;
  font-weight: 500;
  font-size: 14px;
  width: 100%;
  text-align: left;
}
.share-btn i {
  font-size: 18px;
  width: 20px;
  text-align: center;
}
.share-btn.copy-link {
  background-color: #f4f4f6;
  color: #333;
}
.share-btn.copy-link:hover {
  background-color: #ececef;
}
.share-btn.support-request {
  background-color: #5b4fff;
  color: white;
}
.share-btn.support-request:hover {
  background-color: #4a3fe0;
}
.still-need-help-text {
  font-size: 13px;
  color: #777;
  margin-bottom: 12px;
}
.widget {
  margin-bottom: 30px;
}
</style>
